import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Printer, Bluetooth, Monitor, CheckCircle, XCircle, Loader2, Settings, Trash2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { getProviderContact, getProviderDisplayName } from '@/utils/providerContacts';

// ─────────────────────────────────────────────────────────────────────────────
// Native Android Bridge (window.AndroidPrinter) — injected by WebAppActivity.
// Whenever the app runs inside the RecargameYa Android shell, this object
// exists and takes over Bluetooth printing so the connection survives page
// reloads / backgrounding (Web Bluetooth loses GATT in those cases).
// The whole PrinterSettings + printToBluetoothPrinter code paths transparently
// switch to this bridge when present, and fall back to Web Bluetooth otherwise.
// ─────────────────────────────────────────────────────────────────────────────
const _getNativeBridge = () => (typeof window !== 'undefined' ? window.AndroidPrinter : null);
export const isNativeBridgePresent = () => !!_getNativeBridge();
const _nativeParse = (raw) => { try { return JSON.parse(raw || '{}'); } catch { return {}; } };
const nativeIsAvailable = () => {
  const b = _getNativeBridge();
  if (!b) return false;
  try { return String(b.isAvailable()) === 'true'; } catch { return false; }
};
const nativeListBonded = () => {
  const b = _getNativeBridge();
  if (!b) return [];
  try { return JSON.parse(b.listBondedDevices() || '[]'); } catch { return []; }
};
const nativeConnect = (address) => {
  const b = _getNativeBridge();
  if (!b) return { ok: false, error: 'bridge_missing' };
  return _nativeParse(b.connect(address));
};
const nativePrint = (text) => {
  const b = _getNativeBridge();
  if (!b) return { ok: false, error: 'bridge_missing' };
  return _nativeParse(b.print(text));
};
const nativeStatus = () => {
  const b = _getNativeBridge();
  if (!b) return { connected: false, name: '', address: '' };
  return _nativeParse(b.getStatus());
};
const nativeDisconnect = () => {
  const b = _getNativeBridge();
  if (!b) return;
  try { b.disconnect(); } catch { /* ignore */ }
};

const PrinterSettings = ({ isOpen, onClose }) => {
  const [autoPrint, setAutoPrint] = useState(false);
  const [paperSaving, setPaperSaving] = useState(true);
  const [printerType, setPrinterType] = useState('browser'); // 'browser' or 'bluetooth'
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [bluetoothSupported, setBluetoothSupported] = useState(false);

  // Native bridge state
  const [nativeMode, setNativeMode] = useState(false);
  const [bondedDevices, setBondedDevices] = useState([]);
  const [showBondedPicker, setShowBondedPicker] = useState(false);

  // Load saved settings
  useEffect(() => {
    const savedAutoPrint = localStorage.getItem('autoPrintEnabled') === 'true';
    const savedPrinterType = localStorage.getItem('printerType') || 'browser';
    const savedDeviceName = localStorage.getItem('bluetoothDeviceName');
    const savedDeviceId = localStorage.getItem('bluetoothDeviceId');
    const savedNativeAddress = localStorage.getItem('bluetoothNativeAddress');

    setAutoPrint(savedAutoPrint);
    setPaperSaving(localStorage.getItem('printerPaperSaving') !== 'false');
    setPrinterType(savedPrinterType);

    // ── Native bridge detection ──
    const hasNative = isNativeBridgePresent();
    setNativeMode(hasNative);

    if (hasNative) {
      // In native mode the "printer backend" is always considered supported
      // (we don't care about Web Bluetooth capabilities).
      setBluetoothSupported(true);

      if (savedDeviceName) {
        // Reflect saved config with a live status check from the bridge.
        const st = nativeStatus();
        setBluetoothDevice({
          name: savedDeviceName,
          address: savedNativeAddress || st.address || '',
          connected: !!st.connected,
        });
      }
      // Skip Web Bluetooth rehydration in native mode.
      return;
    }

    if (savedDeviceName) {
      setBluetoothDevice({ name: savedDeviceName, connected: false });
    }

    // Check if Web Bluetooth is supported
    if (navigator.bluetooth) {
      setBluetoothSupported(true);
    }

    // ── Silent re-hydration after page reload (Web Bluetooth only) ──
    // If the user already authorized a printer in a previous session, ask
    // the browser for that device via `getDevices()` and silently reconnect
    // GATT — no user prompt required, so the status badge can flip back to
    // "Conectado" (green dot) right after reload instead of staying red
    // until the next print.
    //
    // Available since Chrome 92+ on Android (the platform the users run
    // the TWA from). On older browsers we just leave the badge red until
    // the user prints once — that's still better than the old behavior
    // (prompt on every print).
    let cancelled = false;
    const rehydrate = async () => {
      if (!savedDeviceId) {
        return;
      }
      if (typeof navigator.bluetooth?.getDevices !== 'function') {
        // Browser too old → show user that they need to use the Reconnect button.
        toast.info('Tu Chrome no soporta reconexión automática. Usa el botón "Reconectar" después de recargar.', { duration: 4000 });
        return;
      }
      try {
        if (_btDevice && _btDevice.gatt?.connected && _btCharacteristic) {
          if (!cancelled) {
            setBluetoothDevice({
              name: _btDevice.name || savedDeviceName || 'Impresora Bluetooth',
              device: _btDevice,
              connected: true,
            });
          }
          return;
        }
        const knownDevices = await navigator.bluetooth.getDevices();
        // eslint-disable-next-line no-console
        console.log('[BT] getDevices returned', knownDevices.length, 'devices. Looking for id:', savedDeviceId);
        const match = knownDevices.find((d) => d.id === savedDeviceId);
        if (!match) {
          toast.warning('Permiso de impresora perdido. Toca "Reconectar" para volver a unir.', { duration: 4000 });
          return;
        }
        const server = match.gatt.connected ? match.gatt : await match.gatt.connect();
        const characteristic = await _findWritableCharacteristic(server);
        if (!characteristic) {
          toast.error('No se pudo encontrar la característica de impresión. Toca "Reconectar".', { duration: 4000 });
          return;
        }
        _btDevice = match;
        _btCharacteristic = characteristic;
        match.addEventListener('gattserverdisconnected', () => {
          _btCharacteristic = null;
          setBluetoothDevice((prev) => (prev ? { ...prev, connected: false } : null));
        });
        if (!cancelled) {
          setBluetoothDevice({
            name: match.name || savedDeviceName || 'Impresora Bluetooth',
            device: match,
            connected: true,
          });
          toast.success('Impresora reconectada automáticamente');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[BT] rehydrate failed:', err);
        toast.error('Reconexión automática falló: ' + (err?.message || 'error'), { duration: 4000 });
      }
    };
    if (isOpen) rehydrate();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Save auto-print setting
  const handleAutoPrintChange = (enabled) => {
    setAutoPrint(enabled);
    localStorage.setItem('autoPrintEnabled', enabled.toString());
    toast.success(enabled ? 'Impresión automática activada' : 'Impresión automática desactivada');
  };

  // Save paper-saving setting
  const handlePaperSavingChange = (enabled) => {
    setPaperSaving(enabled);
    localStorage.setItem('printerPaperSaving', enabled.toString());
    toast.success(enabled ? 'Ahorro de papel activado (formato compacto)' : 'Ahorro de papel desactivado (formato completo)');
  };

  // Save printer type
  const handlePrinterTypeChange = (type) => {
    setPrinterType(type);
    localStorage.setItem('printerType', type);
  };

  // Connect to Bluetooth printer
  const connectBluetoothPrinter = async () => {
    // ── Native Android Bridge path ──
    if (nativeMode) {
      if (!nativeIsAvailable()) {
        toast.error('Bluetooth desactivado o permiso denegado. Actívalo y vuelve a intentar.');
        return;
      }
      const list = nativeListBonded();
      if (!list || list.length === 0) {
        toast.error('No hay impresoras emparejadas en el sistema. Empareja la impresora desde Ajustes → Bluetooth y vuelve.', { duration: 6000 });
        return;
      }
      setBondedDevices(list);
      setShowBondedPicker(true);
      return;
    }

    if (!navigator.bluetooth) {
      toast.error('Tu navegador no soporta Bluetooth. Usa Chrome en Android o un navegador compatible.');
      return;
    }

    setIsConnecting(true);

    try {
      // Request Bluetooth device - common thermal printer services
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb', // Generic printer service
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // ESC/POS printer service
          '0000ff00-0000-1000-8000-00805f9b34fb', // Common thermal printer
          '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 BLE module
          '00001101-0000-1000-8000-00805f9b34fb', // Serial Port Profile
        ]
      });

      // Try to connect
      const server = await device.gatt.connect();
      
      // Save device info
      setBluetoothDevice({
        name: device.name || 'Impresora Bluetooth',
        device: device,
        server: server,
        connected: true
      });

      localStorage.setItem('bluetoothDeviceName', device.name || 'Impresora Bluetooth');
      // Persist the device id so we can rehydrate it silently after a page
      // reload via navigator.bluetooth.getDevices() (no prompt needed).
      if (device.id) localStorage.setItem('bluetoothDeviceId', device.id);
      localStorage.setItem('printerType', 'bluetooth');
      setPrinterType('bluetooth');

      // Prime the module-level cache used by `printToBluetoothPrinter` so the
      // very first print doesn't have to re-request the device.
      _btDevice = device;
      _btCharacteristic = await _findWritableCharacteristic(server);

      toast.success(`Conectado a ${device.name || 'Impresora'}`);

      // Handle disconnection
      device.addEventListener('gattserverdisconnected', () => {
        setBluetoothDevice(prev => prev ? { ...prev, connected: false } : null);
        _btCharacteristic = null;
        toast.warning('Impresora desconectada');
      });

    } catch (error) {
      console.error('Bluetooth error:', error);
      if (error.name === 'NotFoundError') {
        toast.error('No se seleccionó ninguna impresora');
      } else if (error.name === 'SecurityError') {
        toast.error('Permiso de Bluetooth denegado');
      } else {
        toast.error('Error al conectar: ' + error.message);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Native bridge: user picks an already-paired device from the OS list.
  const selectBondedDevice = async (device) => {
    setIsConnecting(true);
    try {
      const res = nativeConnect(device.address);
      if (!res.ok) {
        toast.error('No se pudo conectar: ' + (res.error || 'error'));
        return;
      }
      localStorage.setItem('bluetoothDeviceName', device.name || 'Impresora Bluetooth');
      localStorage.setItem('bluetoothNativeAddress', device.address);
      localStorage.setItem('printerType', 'bluetooth');
      setPrinterType('bluetooth');
      setBluetoothDevice({
        name: device.name || 'Impresora Bluetooth',
        address: device.address,
        connected: true,
      });
      setShowBondedPicker(false);
      toast.success(`Conectado a ${device.name || 'Impresora'}`);
    } catch (e) {
      toast.error('Error al conectar: ' + (e?.message || 'desconocido'));
    } finally {
      setIsConnecting(false);
    }
  };

  // Fast reconnect — tries to reuse the previously authorized device. Uses
  // `getDevices()` when available (no prompt). Falls back to `requestDevice`
  // pre-filtered by name so the picker shows just the saved printer with one
  // tap to confirm (much faster than the full picker flow).
  const reconnectBluetoothPrinter = async () => {
    // Native bridge: just call connect(address) — no prompt at all.
    if (nativeMode) {
      const addr = localStorage.getItem('bluetoothNativeAddress');
      const name = localStorage.getItem('bluetoothDeviceName');
      if (!addr) {
        toast.error('No hay impresora guardada. Toca "Conectar" y selecciónala.');
        return;
      }
      setIsConnecting(true);
      try {
        const res = nativeConnect(addr);
        if (!res.ok) {
          toast.error('No se pudo reconectar: ' + (res.error || 'error'));
          return;
        }
        setBluetoothDevice({ name: name || 'Impresora Bluetooth', address: addr, connected: true });
        toast.success('Impresora reconectada');
      } finally {
        setIsConnecting(false);
      }
      return;
    }
    if (!navigator.bluetooth) return;
    const savedId = localStorage.getItem('bluetoothDeviceId');
    const savedName = localStorage.getItem('bluetoothDeviceName');
    setIsConnecting(true);
    try {
      let device = null;
      if (typeof navigator.bluetooth.getDevices === 'function') {
        try {
          const list = await navigator.bluetooth.getDevices();
          if (savedId) device = list.find((d) => d.id === savedId) || null;
        } catch (_e) { /* ignore */ }
      }
      if (!device) {
        // requestDevice still requires a user tap but with filter the prompt
        // shows just the saved printer (1 tap to confirm).
        device = await navigator.bluetooth.requestDevice({
          filters: savedName ? [{ name: savedName }] : undefined,
          acceptAllDevices: savedName ? false : true,
          optionalServices: [
            '000018f0-0000-1000-8000-00805f9b34fb',
            '49535343-fe7d-4ae5-8fa9-9fafd205e455',
            '0000ff00-0000-1000-8000-00805f9b34fb',
            '0000ffe0-0000-1000-8000-00805f9b34fb',
            '00001101-0000-1000-8000-00805f9b34fb',
          ],
        });
      }
      const server = await device.gatt.connect();
      const characteristic = await _findWritableCharacteristic(server);
      if (!characteristic) throw new Error('Característica de impresión no encontrada');
      _btDevice = device;
      _btCharacteristic = characteristic;
      if (device.id) localStorage.setItem('bluetoothDeviceId', device.id);
      if (device.name) localStorage.setItem('bluetoothDeviceName', device.name);
      device.addEventListener('gattserverdisconnected', () => {
        _btCharacteristic = null;
        setBluetoothDevice((prev) => (prev ? { ...prev, connected: false } : null));
      });
      setBluetoothDevice({
        name: device.name || savedName || 'Impresora Bluetooth',
        device,
        connected: true,
      });
      toast.success('Impresora reconectada');
    } catch (err) {
      console.error('Reconnect failed:', err);
      toast.error('No se pudo reconectar: ' + (err?.message || 'error'));
    } finally {
      setIsConnecting(false);
    }
  };


  // Disconnect Bluetooth printer
  const disconnectBluetoothPrinter = () => {
    if (nativeMode) {
      nativeDisconnect();
      setBluetoothDevice(null);
      localStorage.removeItem('bluetoothDeviceName');
      localStorage.removeItem('bluetoothNativeAddress');
      if (printerType === 'bluetooth') {
        setPrinterType('browser');
        localStorage.setItem('printerType', 'browser');
      }
      toast.info('Impresora desconectada');
      return;
    }
    if (bluetoothDevice?.device?.gatt?.connected) {
      bluetoothDevice.device.gatt.disconnect();
    }
    // Also clear the module-level cache so the next print starts fresh.
    _clearBluetoothCache();
    setBluetoothDevice(null);
    localStorage.removeItem('bluetoothDeviceName');
    localStorage.removeItem('bluetoothDeviceId');
    if (printerType === 'bluetooth') {
      setPrinterType('browser');
      localStorage.setItem('printerType', 'browser');
    }
    toast.info('Impresora desconectada');
  };

  // Test print
  const testPrint = async () => {
    if (printerType === 'bluetooth' && bluetoothDevice?.connected) {
      try {
        // Send test print to Bluetooth
        await printToBluetoothPrinter('--- PRUEBA DE IMPRESIÓN ---\n\nRecargameYa\nImpresora configurada correctamente\n\n' + new Date().toLocaleString() + '\n\n\n');
        toast.success('Prueba enviada a la impresora');
      } catch (error) {
        toast.error('Error al imprimir: ' + error.message);
      }
    } else {
      // Browser print test
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Prueba de Impresión</title>
            <style>
              body { font-family: 'Courier New', monospace; text-align: center; padding: 20px; }
            </style>
          </head>
          <body>
            <h2>--- PRUEBA DE IMPRESIÓN ---</h2>
            <p><strong>RecargameYa</strong></p>
            <p>Impresora configurada correctamente</p>
            <p>${new Date().toLocaleString()}</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
      toast.success('Prueba enviada a la impresora');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración de Impresora
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Auto Print Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Impresión Automática</Label>
              <p className="text-sm text-slate-500">
                Imprimir recibo automáticamente al completar transacción
              </p>
            </div>
            <Switch
              checked={autoPrint}
              onCheckedChange={handleAutoPrintChange}
            />
          </div>

          {/* Paper Saving Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Ahorrar Papel</Label>
              <p className="text-sm text-slate-500">
                Activado: formato compacto. Desactivado: imprime el recibo completo como se muestra en pantalla
              </p>
            </div>
            <Switch
              checked={paperSaving}
              onCheckedChange={handlePaperSavingChange}
              data-testid="paper-saving-toggle"
            />
          </div>

          {/* Printer Type Selection */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Tipo de Impresora</Label>
            
            {/* Browser/System Printer */}
            <div 
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                printerType === 'browser' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => handlePrinterTypeChange('browser')}
            >
              <Monitor className={`w-8 h-8 ${printerType === 'browser' ? 'text-blue-500' : 'text-slate-400'}`} />
              <div className="flex-1">
                <p className="font-medium">Impresora del Sistema</p>
                <p className="text-sm text-slate-500">
                  Usa la impresora configurada en tu PC o dispositivo
                </p>
              </div>
              {printerType === 'browser' && (
                <CheckCircle className="w-5 h-5 text-blue-500" />
              )}
            </div>

            {/* Bluetooth Printer */}
            <div 
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                printerType === 'bluetooth' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-slate-300'
              } ${!bluetoothSupported ? 'opacity-50' : ''}`}
              onClick={() => bluetoothSupported && handlePrinterTypeChange('bluetooth')}
            >
              <Bluetooth className={`w-8 h-8 ${printerType === 'bluetooth' ? 'text-blue-500' : 'text-slate-400'}`} />
              <div className="flex-1">
                <p className="font-medium flex items-center gap-2">
                  Impresora Bluetooth
                  {nativeMode && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                      <Smartphone className="w-3 h-3" />
                      Nativo
                    </span>
                  )}
                </p>
                <p className="text-sm text-slate-500">
                  {bluetoothSupported || nativeMode
                    ? 'Conecta una impresora térmica por Bluetooth'
                    : 'No disponible en este navegador'}
                </p>
                {bluetoothDevice && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`w-2 h-2 rounded-full ${bluetoothDevice.connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    <span className="text-xs text-slate-600">{bluetoothDevice.name}</span>
                  </div>
                )}
              </div>
              {printerType === 'bluetooth' && (
                <CheckCircle className="w-5 h-5 text-blue-500" />
              )}
            </div>
          </div>

          {/* Bluetooth Connection Button */}
          {printerType === 'bluetooth' && bluetoothSupported && (
            <div className="space-y-2">
              {!bluetoothDevice?.connected ? (
                <div className="flex gap-2">
                  {/* Fast Reconnect — visible only when we have a saved printer
                      but the connection is down (typical after page reload). */}
                  {bluetoothDevice?.name && (localStorage.getItem('bluetoothDeviceId') || localStorage.getItem('bluetoothNativeAddress')) && (
                    <Button
                      variant="outline"
                      onClick={reconnectBluetoothPrinter}
                      disabled={isConnecting}
                      className="flex-1"
                      data-testid="bluetooth-reconnect-btn"
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Reconectando...
                        </>
                      ) : (
                        <>
                          <Bluetooth className="w-4 h-4 mr-2" />
                          Reconectar
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={connectBluetoothPrinter}
                    disabled={isConnecting}
                    className="flex-1"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Buscando impresoras...
                      </>
                    ) : (
                      <>
                        <Bluetooth className="w-4 h-4 mr-2" />
                        {bluetoothDevice?.name ? 'Cambiar impresora' : 'Conectar Impresora Bluetooth'}
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={disconnectBluetoothPrinter}
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Desconectar
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Bonded devices picker (Native Android Bridge only) */}
          {nativeMode && printerType === 'bluetooth' && showBondedPicker && (
            <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="bonded-devices-picker">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Impresoras emparejadas</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBondedPicker(false)}
                  className="h-7 text-xs"
                >
                  Cancelar
                </Button>
              </div>
              {bondedDevices.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">Sin dispositivos emparejados.</p>
              ) : (
                bondedDevices.map((d) => (
                  <button
                    key={d.address}
                    onClick={() => selectBondedDevice(d)}
                    disabled={isConnecting}
                    className="w-full text-left p-2 rounded-md border border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors flex items-center gap-2 disabled:opacity-50"
                    data-testid={`bonded-device-${d.address}`}
                  >
                    <Bluetooth className="w-4 h-4 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.name || '(sin nombre)'}</p>
                      <p className="text-xs text-slate-500 font-mono">{d.address}</p>
                    </div>
                    {isConnecting && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Test Print Button */}
          <Button 
            variant="outline" 
            onClick={testPrint}
            className="w-full"
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Prueba
          </Button>

          {/* Info */}
          <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
            <p className="font-medium mb-1">💡 Consejos:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Para PC: La impresora debe estar instalada en el sistema</li>
              {nativeMode ? (
                <li>Bluetooth nativo Android — empareja la impresora en Ajustes del sistema y luego selecciónala aquí</li>
              ) : (
                <li>Para Bluetooth: Usa Chrome en Android para mejor compatibilidad</li>
              )}
              <li>Impresoras compatibles: ESC/POS, térmicas de 58mm o 80mm</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ESC/POS Commands
const ESC = 0x1B;
const GS = 0x1D;

// Helper to create ESC/POS commands
const createEscPosCommands = () => ({
  INIT: new Uint8Array([ESC, 0x40]), // Initialize printer
  CENTER: new Uint8Array([ESC, 0x61, 0x01]), // Center align
  LEFT: new Uint8Array([ESC, 0x61, 0x00]), // Left align
  BOLD_ON: new Uint8Array([ESC, 0x45, 0x01]), // Bold on
  BOLD_OFF: new Uint8Array([ESC, 0x45, 0x00]), // Bold off
  DOUBLE_HEIGHT_ON: new Uint8Array([ESC, 0x21, 0x10]), // Double height
  DOUBLE_WIDTH_ON: new Uint8Array([ESC, 0x21, 0x20]), // Double width
  DOUBLE_ON: new Uint8Array([ESC, 0x21, 0x30]), // Double height and width
  NORMAL: new Uint8Array([ESC, 0x21, 0x00]), // Normal size
  CUT_PAPER: new Uint8Array([GS, 0x56, 0x00]), // Full cut
  FEED_LINES: (n) => new Uint8Array([ESC, 0x64, n]), // Feed n lines
});

// Format receipt for thermal printer (58mm/80mm)
export const formatReceiptForThermalPrinter = (transaction, userInfo) => {
  const LINE_WIDTH = 32; // Characters for 58mm printer
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('es-DO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getServiceName = (type) => {
    const types = {
      'recarga_movil': 'Recarga Movil',
      'paquete_internet': 'Paquete Internet',
      'factura': 'Pago de Factura',
      'luz_prepago': 'Luz Prepago',
      'seguro': 'Seguro',
      'activacion_sim': 'Activacion SIM',
      'recarga_internacional': 'Recarga Intl'
    };
    return types[type] || type;
  };

  const centerText = (text) => {
    const padding = Math.max(0, Math.floor((LINE_WIDTH - text.length) / 2));
    return ' '.repeat(padding) + text;
  };

  const formatRow = (label, value) => {
    const maxLabelLen = 14;
    const maxValueLen = LINE_WIDTH - maxLabelLen - 2;
    const truncLabel = label.substring(0, maxLabelLen);
    const truncValue = value.toString().substring(0, maxValueLen);
    const spaces = LINE_WIDTH - truncLabel.length - truncValue.length;
    return truncLabel + ' '.repeat(Math.max(1, spaces)) + truncValue;
  };

  const divider = '-'.repeat(LINE_WIDTH);
  // Paper saving mode (default ON = compact). When OFF, print the full
  // receipt as shown on screen (more details + spacing).
  const paperSaving = localStorage.getItem('printerPaperSaving') !== 'false';
  const thickDivider = '='.repeat(LINE_WIDTH);
  
  const isPrepaidElectricity = transaction.type === 'luz_prepago' || 
    (transaction.provider && (
      transaction.provider.toLowerCase().includes('edesur') ||
      transaction.provider.toLowerCase().includes('edenorte') ||
      transaction.provider.toLowerCase().includes('prepago')
    ));

  const isRemittance = transaction.type === 'remesa' ||
    (transaction.provider && (
      transaction.provider.toLowerCase().includes('moncash') ||
      transaction.provider.toLowerCase().includes('natcash')
    ));
  const prepaidInfo = transaction.prepaid_info || {};
  
  let receipt = '';
  
  // Header (compacto)
  if (isPrepaidElectricity && transaction.provider) {
    receipt += centerText(getProviderDisplayName(transaction.provider)) + '\n';
  }
  receipt += centerText('RECARGAMEYA') + '\n';
  if (!paperSaving) {
    receipt += centerText('Recargas y Pagos') + '\n';
    receipt += '\n';
  }
  if (userInfo?.business_name) {
    receipt += centerText(userInfo.business_name) + '\n';
    if (userInfo?.telefono) {
      receipt += centerText('Tel: ' + userInfo.telefono) + '\n';
    }
    if (userInfo?.direccion) {
      receipt += centerText(userInfo.direccion.substring(0, LINE_WIDTH)) + '\n';
    }
  }
  receipt += (paperSaving ? divider : thickDivider) + '\n';
  if (!paperSaving) {
    receipt += centerText('RECIBO DE TRANSACCION') + '\n';
    receipt += thickDivider + '\n';
  }
  
  // Transaction details
  receipt += formatRow('Fecha:', formatDate(transaction.created_at)) + '\n';
  receipt += formatRow('Trans #:', transaction.reference || '-') + '\n';
  receipt += divider + '\n';
  
  if (isRemittance) {
    // Remittance receipt (Haiti — MonCash / NatCash / manual processor)
    const amt = Number(transaction.amount || 0);
    const fee = Number(transaction.service_fee || 0);
    const net = Number(transaction.net_amount || 0);
    const rate = Number(transaction.exchange_rate || 0);
    const received = Number(transaction.amount_received || 0);
    let mode = transaction.commission_mode;
    if (mode !== 'aparte' && mode !== 'descontada' && fee > 0) {
      if (Math.abs(net - amt) < 0.5) mode = 'aparte';
      else if (Math.abs(net - (amt - fee)) < 0.5) mode = 'descontada';
    }
    const isAparte = mode === 'aparte';
    const totalCobrar = isAparte ? amt + fee : amt;

    if (transaction.recipient_name) {
      receipt += formatRow('Recibe:', transaction.recipient_name.substring(0, 18)) + '\n';
    }
    if (transaction.recipient_phone) {
      receipt += formatRow('Tel Rec:', transaction.recipient_phone) + '\n';
    }
    receipt += formatRow('Proveedor:', getProviderDisplayName(transaction.provider) || 'MONCASH') + '\n';
    receipt += divider + '\n';

    receipt += formatRow('Monto Env:', `RD$ ${amt.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
    if (isAparte && fee > 0) {
      receipt += formatRow('+ Comision:', `RD$ ${fee.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
    }
    receipt += divider + '\n';
    receipt += centerText('TOTAL A COBRAR') + '\n';
    receipt += centerText(`RD$ ${totalCobrar.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
    receipt += divider + '\n';
    receipt += centerText('MONTO A RECIBIR') + '\n';
    receipt += centerText(`${received.toLocaleString('es-DO', { minimumFractionDigits: 2 })} ${transaction.currency_received || 'HTG'}`) + '\n';

    if (!paperSaving) {
      if (rate > 0) {
        receipt += formatRow('Tasa:', `${rate.toFixed(4)} ${transaction.currency_received || 'HTG'}/RD$`) + '\n';
      }
      if (mode === 'aparte' || mode === 'descontada') {
        receipt += formatRow('Modalidad:', mode === 'aparte' ? 'APARTE' : 'DESCONTADA') + '\n';
      }
    }
  } else if (isPrepaidElectricity) {
    receipt += formatRow('Contrato:', prepaidInfo.contrato || transaction.account_number || '-') + '\n';
    receipt += formatRow('Cliente:', (prepaidInfo.cliente || '-').substring(0, 18)) + '\n';
    if (prepaidInfo.medidor) {
      receipt += formatRow('Medidor:', prepaidInfo.medidor) + '\n';
    }
    if (prepaidInfo.tarifa) {
      receipt += formatRow('Tarifa:', prepaidInfo.tarifa) + '\n';
    }
    receipt += divider + '\n';
    receipt += formatRow('Medio Pago:', 'Efectivo') + '\n';
    receipt += centerText('MONTO DINERO') + '\n';
    receipt += centerText(`RD$ ${(transaction.amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
    
    if (prepaidInfo.kwh_recargados) {
      receipt += formatRow('Monto Energia:', prepaidInfo.kwh_recargados) + '\n';
    }
    
    // Token - Most important for prepaid
    if (prepaidInfo.token) {
      receipt += divider + '\n';
      receipt += centerText('*** TOKEN DE RECARGA ***') + '\n';
      receipt += centerText(prepaidInfo.token) + '\n';
      receipt += centerText('Guarde este codigo') + '\n';
      receipt += divider + '\n';
    }
    
    if (prepaidInfo.total_mes) {
      receipt += formatRow('Energia Mes:', prepaidInfo.total_mes + ' kWh') + '\n';
    }
  } else {
    // Standard receipt
    const isGameTopup = transaction.type === 'juegos_topup';
    if (isGameTopup) {
      // Game direct topup receipt — recibo entregable al cliente final.
      // Solo imprime datos del juego + MONTO PAGADO (precio al cliente).
      // El banca ve su costo/ganancia en el modal de confirmación previo.
      receipt += formatRow('Servicio:', 'Recarga de Juego') + '\n';
      if (transaction.game_name) {
        receipt += formatRow('Juego:', String(transaction.game_name).substring(0, 18)) + '\n';
      }
      if (transaction.product_name) {
        receipt += formatRow('Paquete:', String(transaction.product_name).substring(0, 18)) + '\n';
      }
      const playerUid = transaction.player_uid || (transaction.juegos && transaction.juegos.user_id);
      if (playerUid) {
        receipt += formatRow('UID:', String(playerUid)) + '\n';
      }
      const playerNick = transaction.player_nickname || (transaction.juegos && transaction.juegos.nickname);
      if (playerNick) {
        receipt += formatRow('Jugador:', String(playerNick).substring(0, 18)) + '\n';
      }
      if (transaction.external_transaction_id) {
        receipt += formatRow('ID Orden:', String(transaction.external_transaction_id).substring(0, 18)) + '\n';
      }
      receipt += divider + '\n';
      receipt += centerText('MONTO PAGADO') + '\n';
      receipt += centerText(`RD$ ${(transaction.amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
    } else {
      receipt += formatRow('Servicio:', getServiceName(transaction.type)) + '\n';
      receipt += formatRow('Proveedor:', getProviderDisplayName(transaction.provider) || '-') + '\n';
      if (transaction.phone_number) {
        receipt += formatRow('Telefono:', transaction.phone_number) + '\n';
      }
      if (transaction.account_number) {
        receipt += formatRow('Cuenta:', transaction.account_number) + '\n';
      }
      receipt += divider + '\n';
      receipt += centerText('MONTO PAGADO') + '\n';
      receipt += centerText(`RD$ ${(transaction.amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
      if (!paperSaving) {
        const comm = Number(transaction.commission || transaction.user_commission || 0);
        const charged = Number(transaction.amount_charged || 0);
        if (comm > 0 || charged > 0) {
          receipt += divider + '\n';
          if (comm > 0) {
            receipt += formatRow('Comision:', `RD$ ${comm.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
          }
          if (charged > 0) {
            receipt += formatRow('Total Cobrado:', `RD$ ${charged.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`) + '\n';
          }
        }
      }
    }
  }
  
  receipt += divider + '\n';
  receipt += formatRow('Estado:', transaction.status === 'completado' ? 'COMPLETADO' : transaction.status?.toUpperCase() || '-') + '\n';
  
  // Footer
  receipt += (paperSaving ? divider : thickDivider) + '\n';
  if (isPrepaidElectricity) {
    const contact = getProviderContact(transaction.provider);
    receipt += centerText(`Inconvenientes ${contact.name || ''}: ${contact.phone}`.trim()) + '\n';
  }
  receipt += centerText('Gracias por su preferencia!') + '\n';
  if (!paperSaving) {
    receipt += centerText('RecargameYa') + '\n';
    receipt += centerText('Conserve este recibo') + '\n';
    receipt += '\n\n\n\n';
  } else {
    receipt += '\n\n'; // Feed mínimo para corte
  }
  
  return receipt;
};

// Helper function to print to Bluetooth (exported for use in ReceiptModal)
// ─────────────────────────────────────────────────────────────────────────────
// Bluetooth connection cache — persists across prints within the same page
// session. Eliminates the "device picker every print" + "disconnect after first
// print" issue the users were reporting.
//
// Strategy:
//   • On the FIRST print, request the device (which prompts the user once),
//     remember its `id` in localStorage, and keep the device + characteristic
//     in module-level variables.
//   • On SUBSEQUENT prints, reuse the cached device. If GATT got dropped
//     (sleep, signal loss, etc.) we silently reconnect without prompting.
//   • If the page reloaded (cache gone) AND the browser supports
//     `navigator.bluetooth.getDevices()`, we resolve the device silently from
//     its saved id — no user gesture required.
//   • If neither path works, fall back to the legacy `requestDevice` prompt
//     so the user can re-pair.
// ─────────────────────────────────────────────────────────────────────────────

const BT_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '00001101-0000-1000-8000-00805f9b34fb',
];

let _btDevice = null;
let _btCharacteristic = null;

const _resetBtCache = () => {
  _btDevice = null;
  _btCharacteristic = null;
};

const _findWritableCharacteristic = async (server) => {
  for (const serviceUUID of BT_SERVICE_UUIDS) {
    try {
      const service = await server.getPrimaryService(serviceUUID);
      const characteristics = await service.getCharacteristics();
      for (const char of characteristics) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          return char;
        }
      }
    } catch (_e) {
      continue;
    }
  }
  return null;
};

const _ensureBluetoothConnection = async () => {
  // Path 1: cached device with live connection → reuse.
  if (_btDevice && _btDevice.gatt?.connected && _btCharacteristic) {
    return { device: _btDevice, characteristic: _btCharacteristic };
  }

  // Path 2: cached device object but GATT dropped → silent reconnect.
  if (_btDevice && _btDevice.gatt && !_btDevice.gatt.connected) {
    const server = await _btDevice.gatt.connect();
    const characteristic = await _findWritableCharacteristic(server);
    if (!characteristic) throw new Error('No se encontró característica de escritura en la impresora');
    _btCharacteristic = characteristic;
    return { device: _btDevice, characteristic };
  }

  // Path 3: page reloaded, try to silently rehydrate the device from id.
  const savedId = localStorage.getItem('bluetoothDeviceId');
  if (savedId && typeof navigator.bluetooth.getDevices === 'function') {
    try {
      const knownDevices = await navigator.bluetooth.getDevices();
      const match = knownDevices.find((d) => d.id === savedId);
      if (match) {
        const server = await match.gatt.connect();
        const characteristic = await _findWritableCharacteristic(server);
        if (characteristic) {
          _btDevice = match;
          _btCharacteristic = characteristic;
          match.addEventListener('gattserverdisconnected', () => {
            _btCharacteristic = null;
          });
          return { device: match, characteristic };
        }
      }
    } catch (_e) {
      // Fall through to the prompt below.
    }
  }

  // Path 4: last resort — prompt the user (requires a user gesture).
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: BT_SERVICE_UUIDS,
  });
  const server = await device.gatt.connect();
  const characteristic = await _findWritableCharacteristic(server);
  if (!characteristic) throw new Error('No se encontró característica de escritura en la impresora');

  _btDevice = device;
  _btCharacteristic = characteristic;
  if (device.id) localStorage.setItem('bluetoothDeviceId', device.id);
  if (device.name) localStorage.setItem('bluetoothDeviceName', device.name);

  device.addEventListener('gattserverdisconnected', () => {
    _btCharacteristic = null;
  });

  return { device, characteristic };
};

export const printToBluetoothPrinter = async (text, isEscPos = false) => {
  // ── Native Android Bridge path (survives page reloads / backgrounding) ──
  if (isNativeBridgePresent()) {
    const savedAddress = localStorage.getItem('bluetoothNativeAddress');
    if (!savedAddress) {
      throw new Error('No hay impresora Bluetooth configurada');
    }

    // Detectar errores de socket muerto en el side nativo. La APK reporta el
    // mensaje crudo de Android ("read failed, socket might closed or
    // timeout, read ret: -1", "Broken pipe", "Connection reset by peer",
    // etc.). `nativeStatus().connected` NO es confiable porque solo lee un
    // flag en memoria del service — el socket RFCOMM puede estar muerto sin
    // que el flag se actualice (ej. la impresora se durmió).
    const _isStaleSocketError = (msg) => {
      if (!msg) return false;
      const s = String(msg).toLowerCase();
      return (
        s.includes('read failed') ||
        s.includes('read ret: -1') ||
        s.includes('socket might closed') ||
        s.includes('socket closed') ||
        s.includes('broken pipe') ||
        s.includes('connection reset') ||
        s.includes('not connected') ||
        s.includes('connection abort') ||
        s.includes('timeout')
      );
    };

    const _tryPrint = async () => {
      const st = nativeStatus();
      if (!st.connected) {
        const conn = nativeConnect(savedAddress);
        if (!conn.ok) {
          throw new Error(conn.error || 'No se pudo conectar a la impresora');
        }
      }
      const res = nativePrint(text);
      if (!res.ok) {
        throw new Error(res.error || 'Error al imprimir');
      }
      return true;
    };

    try {
      return await _tryPrint();
    } catch (err) {
      // Fix 25-Oct-2026: ante un socket muerto (típico cuando la impresora
      // se durmió o se cerró tras unos minutos), forzar reconexión completa
      // y reintentar UNA sola vez antes de propagar el error al usuario.
      if (_isStaleSocketError(err?.message)) {
        try { nativeDisconnect(); } catch (_e) { /* ignore */ }
        // Pequeño delay para que Android libere el socket antes del
        // reconnect — sin esto el BluetoothAdapter puede devolver el
        // mismo handle muerto.
        await new Promise((r) => setTimeout(r, 400));
        const reconn = nativeConnect(savedAddress);
        if (!reconn.ok) {
          throw new Error(
            `No se pudo reconectar a la impresora: ${reconn.error || 'desconocido'}`
          );
        }
        // Delay extra tras reconectar para que el firmware del printer
        // acepte comandos (algunos printers chinos necesitan ~500ms).
        await new Promise((r) => setTimeout(r, 300));
        const res2 = nativePrint(text);
        if (!res2.ok) {
          throw new Error(
            `Impresora no responde tras reconectar. Verifica que esté encendida, con papel y cerca del teléfono. (${res2.error || 'sin detalle'})`
          );
        }
        return true;
      }
      throw err;
    }
  }

  const deviceName = localStorage.getItem('bluetoothDeviceName');
  if (!deviceName) {
    throw new Error('No hay impresora Bluetooth configurada');
  }

  try {
    const { characteristic } = await _ensureBluetoothConnection();
    const commands = createEscPosCommands();

    // Initialize printer
    await characteristic.writeValue(commands.INIT);
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Convert text to bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    // Send in chunks (BLE has MTU limit)
    const chunkSize = 20;
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      await characteristic.writeValue(chunk);
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    // Feed and cut paper
    await characteristic.writeValue(commands.FEED_LINES(3));
    await new Promise((resolve) => setTimeout(resolve, 50));
    await characteristic.writeValue(commands.CUT_PAPER);

    // IMPORTANT: do NOT disconnect here. Keeping the GATT connection alive
    // is what lets subsequent prints reuse the link without prompting the
    // user again. The connection is dropped automatically when the page
    // closes or the user manually disconnects from PrinterSettings.
    return true;
  } catch (error) {
    // If the failure was a stale handle (NetworkError / disconnected), drop
    // the cache so the next attempt rebuilds from scratch.
    if (error && (error.name === 'NetworkError' || /disconnected|GATT/i.test(error.message || ''))) {
      _resetBtCache();
    }
    console.error('Bluetooth print error:', error);
    throw error;
  }
};

// Exposed so PrinterSettings.disconnect can clear the module-level cache too.
export const _clearBluetoothCache = () => {
  if (_btDevice?.gatt?.connected) {
    try { _btDevice.gatt.disconnect(); } catch (_e) { /* ignore */ }
  }
  _resetBtCache();
};

// Print receipt using browser (window.print) - for PC/USB printers
export const printReceiptBrowser = (receiptHtml) => {
  const printWindow = window.open('', '_blank', 'width=300,height=600');
  if (!printWindow) {
    throw new Error('No se pudo abrir ventana de impresión. Verifica los bloqueadores de popups.');
  }
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Recibo</title>
        <style>
          @media print {
            @page { 
              size: 80mm auto; 
              margin: 0; 
            }
            body { 
              margin: 0; 
              padding: 5px;
            }
          }
          body {
            font-family: 'Courier New', Courier, monospace;
            font-size: 12px;
            width: 80mm;
            margin: 0 auto;
            padding: 5px;
          }
          .receipt-container {
            width: 100%;
          }
          .text-center { text-align: center; }
          .text-bold { font-weight: bold; }
          .text-large { font-size: 16px; }
          .divider { 
            border-top: 1px dashed #000; 
            margin: 5px 0; 
          }
          .row {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
          }
          .token-box {
            border: 2px solid #000;
            padding: 8px;
            margin: 8px 0;
            text-align: center;
          }
          .token-value {
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 1px;
          }
        </style>
      </head>
      <body>
        ${receiptHtml}
      </body>
    </html>
  `);
  printWindow.document.close();
  
  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.print();
    printWindow.close();
  };
  
  // Fallback for browsers that don't fire onload
  setTimeout(() => {
    if (!printWindow.closed) {
      printWindow.print();
      printWindow.close();
    }
  }, 500);
};

// Auto print handler - call this after successful transaction
export const handleAutoPrint = async (transaction, userInfo) => {
  if (!isAutoPrintEnabled()) {
    return { printed: false, reason: 'auto_print_disabled' };
  }
  
  const printerType = getPrinterType();
  
  try {
    if (printerType === 'bluetooth') {
      const receiptText = formatReceiptForThermalPrinter(transaction, userInfo);
      await printToBluetoothPrinter(receiptText);
      return { printed: true, method: 'bluetooth' };
    } else {
      // Browser/system printer - we'll return info to let the component handle it
      return { printed: false, reason: 'browser_print_needs_ui', method: 'browser' };
    }
  } catch (error) {
    console.error('Auto print error:', error);
    return { printed: false, reason: 'error', error: error.message };
  }
};

// Check if auto print is enabled
export const isAutoPrintEnabled = () => {
  return localStorage.getItem('autoPrintEnabled') === 'true';
};

// Get printer type
export const getPrinterType = () => {
  return localStorage.getItem('printerType') || 'browser';
};

export default PrinterSettings;
